import type { AudioFramePayload } from "./aiHub/types";
import {
  RHYTHM_RAW_PEAK_THRESHOLD,
  rawFrequencyPeak,
} from "./deriveRhythmAmplitude";

export const RHYTHM_SPECTRUM_BINS = 32;

export type RhythmAudioFrameInput = {
  frequencyData: number[];
  currentAmplitude: number;
  rms?: number;
  bass?: number;
  mids?: number;
  treble?: number;
  rhythmSpectrum?: number[];
  beat?: boolean;
  timestamp?: number;
};

let emaSpectrum: number[] = new Array(RHYTHM_SPECTRUM_BINS).fill(0);
let lastBeatEnergy = 0;

function bandAvg(values: number[], start: number, end: number): number {
  const s = Math.max(0, Math.floor(start));
  const e = Math.min(values.length, Math.ceil(end));
  if (e <= s) return 0;
  let sum = 0;
  for (let i = s; i < e; i += 1) sum += values[i] ?? 0;
  return sum / (e - s);
}

/** 128-bin FFT (0-255) → 32-bin 峰值归一化频谱（对齐 ai-electron / ai-hub） */
export function downsampleRhythmSpectrum(frequencyData: number[]): number[] {
  const n = frequencyData.length;
  if (n <= 0) return [];

  if (rawFrequencyPeak(frequencyData) < RHYTHM_RAW_PEAK_THRESHOLD) {
    return new Array(RHYTHM_SPECTRUM_BINS).fill(0);
  }

  const spectrum: number[] = [];
  for (let band = 0; band < RHYTHM_SPECTRUM_BINS; band += 1) {
    const start = Math.floor((band / RHYTHM_SPECTRUM_BINS) * n);
    const end = Math.max(
      start + 1,
      Math.floor(((band + 1) / RHYTHM_SPECTRUM_BINS) * n)
    );
    let peak = 0;
    for (let i = start; i < end; i += 1) {
      peak = Math.max(peak, (frequencyData[i] ?? 0) / 255);
    }
    spectrum.push(peak);
  }

  const peak = Math.max(...spectrum, 0.0001);
  return spectrum.map((value) => Math.min(1, value / peak));
}

/**
 * 将宿主频谱帧整合为 ai-hub 律动引擎可用的 AudioFramePayload。
 * 对齐 ai-electron audioRhythmService.enrichFrame。
 */
export function buildRhythmAudioFrame(
  input: RhythmAudioFrameInput,
  options?: { sensitivity?: number; resetEma?: boolean }
): AudioFramePayload {
  if (options?.resetEma) {
    emaSpectrum = new Array(RHYTHM_SPECTRUM_BINS).fill(0);
    lastBeatEnergy = 0;
  }

  const timestamp = input.timestamp ?? Date.now();

  // 桌面端 audio-frame-builder 已 enrich 时勿二次 gain/EMA（对齐 ai-electron 单次 enrichFrame）
  const preEnriched =
    input.rhythmSpectrum?.length === RHYTHM_SPECTRUM_BINS &&
    input.rms !== undefined;

  if (preEnriched) {
    const spectrum = input.rhythmSpectrum!;
    const rms = Math.min(1, input.rms ?? input.currentAmplitude);
    const rawPeak = rawFrequencyPeak(input.frequencyData);
    if (
      !input.frequencyData?.length ||
      rawPeak < RHYTHM_RAW_PEAK_THRESHOLD ||
      rms < 0.01
    ) {
      resetRhythmAudioFrameState();
      return {
        timestamp,
        rms: 0,
        bass: 0,
        mids: 0,
        treble: 0,
        spectrum: new Array(RHYTHM_SPECTRUM_BINS).fill(0),
        beat: false,
        source: "system",
      };
    }
    return {
      timestamp,
      rms,
      bass: Math.min(1, input.bass ?? bandAvg(spectrum, 0, 6)),
      mids: Math.min(1, input.mids ?? bandAvg(spectrum, 6, 18)),
      treble: Math.min(
        1,
        input.treble ?? bandAvg(spectrum, 18, RHYTHM_SPECTRUM_BINS)
      ),
      spectrum,
      beat: input.beat ?? false,
      source: "system",
    };
  }

  const sensitivity = Math.max(0, Math.min(100, options?.sensitivity ?? 75));
  const gain = 0.5 + sensitivity / 100;

  const rawSpectrum =
    input.rhythmSpectrum?.length === RHYTHM_SPECTRUM_BINS
      ? input.rhythmSpectrum
      : downsampleRhythmSpectrum(input.frequencyData);

  const rmsRawEarly =
    input.rms ??
    Math.max(
      input.currentAmplitude,
      bandAvg(
        input.frequencyData.map((v) => v / 255),
        0,
        input.frequencyData.length
      ) * 1.1
    );
  if (
    rawFrequencyPeak(input.frequencyData) < RHYTHM_RAW_PEAK_THRESHOLD ||
    rmsRawEarly < 0.01
  ) {
    resetRhythmAudioFrameState();
    return {
      timestamp,
      rms: 0,
      bass: 0,
      mids: 0,
      treble: 0,
      spectrum: new Array(RHYTHM_SPECTRUM_BINS).fill(0),
      beat: false,
      source: "system",
    };
  }

  const spectrum = rawSpectrum.map((value, index) => {
    const next = Math.min(1, value * gain);
    const smoothed = (emaSpectrum[index] ?? 0) * 0.45 + next * 0.55;
    emaSpectrum[index] = smoothed;
    return smoothed;
  });

  const rmsRaw =
    input.rms ??
    Math.max(
      input.currentAmplitude,
      bandAvg(
        input.frequencyData.map((v) => v / 255),
        0,
        input.frequencyData.length
      ) * 1.1
    );
  const rms = Math.min(1, rmsRaw * gain);

  const bass =
    input.bass !== undefined
      ? Math.min(1, input.bass * gain)
      : Math.min(1, bandAvg(spectrum, 0, 6) * gain);
  const mids =
    input.mids !== undefined
      ? Math.min(1, input.mids * gain)
      : Math.min(1, bandAvg(spectrum, 6, 18) * gain);
  const treble =
    input.treble !== undefined
      ? Math.min(1, input.treble * gain)
      : Math.min(1, bandAvg(spectrum, 18, RHYTHM_SPECTRUM_BINS) * gain);

  const energy =
    spectrum.reduce((sum, value) => sum + value, 0) /
    Math.max(1, spectrum.length);
  const beatFromEnergy = energy - lastBeatEnergy > 0.12 && rms > 0.03;
  lastBeatEnergy = energy * 0.9 + lastBeatEnergy * 0.1;

  return {
    timestamp,
    rms,
    bass,
    mids,
    treble,
    spectrum,
    beat: input.beat ?? beatFromEnergy,
    source: "system",
  };
}

/** 华奋达 setMusicData 振幅参数：1~7（对齐 ai-hub huafendaRhythmAdapter） */
export function resolveHuafendaMusicAmplitude(
  rms: number,
  fallback = 0
): number {
  const value = rms > 0 ? rms : fallback;
  if (value <= 0.001) return 0;
  return Math.min(7, Math.max(1, Math.round(value * 7)));
}

export function resetRhythmAudioFrameState(): void {
  emaSpectrum = new Array(RHYTHM_SPECTRUM_BINS).fill(0);
  lastBeatEnergy = 0;
}
